import store from '@/store'
import useJwt from '@/cubus/jwt/useJwt'

function isChatbotAllowed() {
  const sess = store.getters['cubus-store/SESSION']
  if (!sess || !sess.user) return false
  if (sess.user.sid === 'root') return true
  const { roles } = sess.user
  return roles && roles.some(r => r.sid === 'admin' || r.sid === 'dev')
}

function waitForSession() {
  const sess = store.getters['cubus-store/SESSION']
  if (sess && sess.user) return Promise.resolve()
  // PROP-086: восстановление сессии по httpOnly-cookie (токена в localStorage больше нет).
  return useJwt.whoami({}).then(response => {
    if (response.data && response.data.session) {
      store.commit('cubus-store/SET_SESSION', response.data.session)
    } else {
      throw new Error('no session')
    }
  })
}

export default [
  {
    path: '/cubus/admin/bulk-users',
    name: 'cubus-bulk-users',
    component: () => import('@/cubus/pages/admin/CbsBulkUsers.vue'),
    beforeEnter: (to, from, next) => {
      waitForSession().then(() => { if (isChatbotAllowed()) next(); else next('/') }).catch(() => next('/'))
    },
  },
  {
    // PROP-218: кто сейчас в домене. Гейт тот же, что у прочих админских страниц:
    // список работающих людей вместе со временем их работы — не для рядового пользователя.
    path: '/cubus/admin/sessions',
    name: 'cubus-active-sessions',
    component: () => import('@/cubus/pages/admin/CbsActiveSessions.vue'),
    beforeEnter: (to, from, next) => {
      waitForSession().then(() => { if (isChatbotAllowed()) next(); else next('/') }).catch(() => next('/'))
    },
  },
  {
    path: '/cubus/constructor/ref-availability',
    name: 'cubus-ref-availability',
    component: () => import('@/cubus/pages/constructor/CbsRefAvailability.vue'),
    beforeEnter: (to, from, next) => {
      waitForSession().then(() => { if (isChatbotAllowed()) next(); else next('/') }).catch(() => next('/'))
    },
  },
  {
    path: '/cubus/ai-assistant',
    name: 'cubus-ai-assistant',
    component: () => import('@/cubus/pages/ai-assistant/CbsAiAssistant.vue'),
  },
  {
    path: '/cubus/bpm/launch',
    name: 'cubus-bpm-launch',
    component: () => import('@/cubus/pages/bpm/CbsBpmLaunch.vue'),
    meta: { title: 'Запустить процесс' },
  },
  {
    path: '/cubus/bpm/inbox',
    name: 'cubus-bpm-inbox',
    component: () => import('@/cubus/pages/bpm/CbsBpmInbox.vue'),
    meta: { title: 'Мои задачи' },
  },
  {
    path: '/cubus/bpm/task/:si',
    name: 'cubus-bpm-task',
    component: () => import('@/cubus/pages/bpm/CbsBpmTask.vue'),
    meta: { title: 'Задача' },
  },
  {
    path: '/cubus/bpm/monitor',
    name: 'cubus-bpm-monitor',
    component: () => import('@/cubus/pages/bpm/CbsBpmMonitor.vue'),
    meta: { title: 'Мониторинг процессов' },
  },
  {
    path: '/cubus/bpm/designer',
    name: 'cubus-bpm-designer',
    component: () => import('@/cubus/pages/bpm/CbsBpmDesigner.vue'),
    meta: { title: 'Дизайнер процессов' },
  },
  {
    path: '/cubus/bpm/chains',
    name: 'cubus-bpm-chains',
    component: () => import('@/cubus/pages/bpm/CbsBpmChains.vue'),
    meta: { title: 'Список цепочек' },
  },
  {
    path: '/cubus/chatbot',
    name: 'cubus-chatbot',
    component: () => import('@/cubus/components/chatbot/CbsChatbot.vue'),
    meta: {
      title: 'Chatbot',
    },
    beforeEnter(to, from, next) {
      waitForSession()
        .then(() => {
          if (isChatbotAllowed()) next()
          else next({ name: 'dashboard-cubus' })
        })
        .catch(() => {
          next({ name: 'auth-login' })
        })
    },
  },
  {
    path: '/cubus/constructor',
    name: 'cubus-constructor',
    component: () => import('@/cubus/pages/constructor/CbsConstructor.vue'),
    meta: {
      title: 'Constructor',
    },
    beforeEnter(to, from, next) {
      waitForSession()
        .then(() => {
          if (isChatbotAllowed()) next()
          else next({ name: 'dashboard-cubus' })
        })
        .catch(() => {
          next({ name: 'auth-login' })
        })
    },
  },
  {
    path: '/cubus/objects',
    name: 'cubus-objects',
    component: () => import('@/cubus/components/objects/CbsObjects.vue'),
    meta: {
      title: 'Objects',
    },
  },
  {
    path: '/cubus/domains',
    name: 'cubus-domains',
    component: () => import('@/cubus/components/domains/CbsDomains.vue'),
  },
  {
    path: '/cubus/datasources',
    name: 'cubus-datasources',
    component: () => import('@/cubus/components/datasources/CbsDatasources.vue'),
  },
  {
    path: '/cubus/actions',
    name: 'cubus-actions',
    component: () => import('@/cubus/components/actions/CbsActions.vue'),
  },
  {
    path: '/cubus/object/:objectid',
    name: 'cubus-object',
    component: () => import('@/cubus/components/object/CbsObjectView.vue'),
  },
  {
    path: '/cubus/ribbon',
    name: 'cubus-ribbon',
    component: () => import('@/cubus/pages/profile/CbsRibbon.vue'),
  },
  {
    path: '/cubus/refs',
    name: 'cubus-refs',
    component: () => import('@/cubus/components/object-catalog/CbsObjectCatalog.vue'),
  },
  {
    path: '/cubus/tables',
    name: 'cubus-tables',
    component: () => import('@/cubus/components/object-table/CbsObjectsTable.vue'),
  },
  {
    path: '/cubus/cubes',
    name: 'cubus-cubes',
    component: () => import('@/cubus/components/object-cube/CbsObjectCube.vue'),
  },
  {
    path: '/cubus/imports',
    name: 'cubus-imports',
    component: () => import('@/cubus/components/object-import/CbsObjectsImport.vue'),
  },
  {
    path: '/cubus/reports',
    name: 'cubus-reports',
    component: () => import('@/cubus/components/object-report/CbsObjectsReport.vue'),
  },
  {
    path: '/cubus/procedures',
    name: 'cubus-procedures',
    component: () => import('@/cubus/components/object-procedure/CbsObjectsProcedure.vue'),
  },
  {
    path: '/cubus/mounts',
    name: 'cubus-mounts',
    component: () => import('@/cubus/components/object-mount/CbsObjectsMount.vue'),
  },
  {
    path: '/cubus/domain-skeleton',
    name: 'cubus-domain-skeleton',
    component: () => import('@/cubus/pages/domain-skeleton/CbsDomainSkeleton.vue'),
    meta: {
      title: 'Domain Skeleton',
    },
  },
  {
    path: '/cubus/recordpage/:object/:record',
    name: 'record-page',
    component: () => import('@/cubus/components/record-page/CbsRecordPage.vue'),
    meta: {
      layout: 'full',
    },
  },
  /*
  {
    path: '/cubus/todo',
    name: 'cubus-todos',
    component: () => import('@/cubus/components/todo/CbsTodo.vue'),
  },
  */

  // *===============================================---*
  // *--------- TODO & IT'S FILTERS N TAGS ---------------------------------------*
  // *===============================================---*
  {
    path: '/cubus/todo',
    name: 'cubus-todo',
    component: () => import('@/cubus/components/todo/CbsTodo.vue'),
    meta: {
      contentRenderer: 'sidebar-left',
      contentClass: 'todo-application',
    },
  },
  {
    path: '/cubus/todo/:filter',
    name: 'cubus-todo-filter',
    component: () => import('@/cubus/components/todo/CbsTodo.vue'),
    meta: {
      contentRenderer: 'sidebar-left',
      contentClass: 'todo-application',
      navActiveLink: 'cubus-todo',
    },
    beforeEnter(to, _, next) {
      if (['important', 'completed', 'deleted'].includes(to.params.filter)) next()
      else next({ name: 'error-404' })
    },
  },
  {
    path: '/cubus/todo/tag/:tag',
    name: 'cubus-todo-tag',
    component: () => import('@/cubus/components/todo/CbsTodo.vue'),
    meta: {
      contentRenderer: 'sidebar-left',
      contentClass: 'todo-application',
      navActiveLink: 'cubus-todo',
    },
    beforeEnter(to, _, next) {
      if (['team', 'low', 'medium', 'high', 'update'].includes(to.params.tag)) next()
      else next({ name: 'error-404' })
    },
  },

]
